import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import {
  ChevronLeft, ChevronRight, ChevronDown, Plus, Minus, Check,
  ArrowUp, ArrowDown, ArrowLeftIcon, ArrowRightIcon, Droplets, AlertTriangle, Circle
} from 'lucide-react';

export const HoleScoringView = ({ hole, holeNumber, totalHoles, existingScore, draftScores, onDraftChange, onSave, onPrev, onNext }) => {
  const draft = draftScores[holeNumber];
  const [strokes, setStrokes] = useState(draft?.strokes ?? existingScore?.strokes ?? hole.par);
  const [putts, setPutts] = useState(draft?.putts ?? existingScore?.putts ?? 2);
  const [drive, setDrive] = useState(draft?.drive_accuracy ?? existingScore?.drive_accuracy ?? 'hit');
  const [penaltyH2O, setPenaltyH2O] = useState(draft?.penalty_details?.h2o ?? existingScore?.penalty_details?.h2o ?? 0);
  const [penaltyOB, setPenaltyOB] = useState(draft?.penalty_details?.ob ?? existingScore?.penalty_details?.ob ?? 0);
  const [penaltyDropped, setPenaltyDropped] = useState(draft?.penalty_details?.dropped ?? existingScore?.penalty_details?.dropped ?? 0);
  const [bunkerGreenside, setBunkerGreenside] = useState(draft?.bunker_details?.greenside ?? existingScore?.bunker_details?.greenside ?? 0);
  const [bunkerFairway, setBunkerFairway] = useState(draft?.bunker_details?.fairway ?? existingScore?.bunker_details?.fairway ?? 0);
  const [teeClub, setTeeClub] = useState(draft?.tee_club ?? existingScore?.tee_club ?? '');
  const [firstPuttDist, setFirstPuttDist] = useState(draft?.first_putt_distance ?? existingScore?.first_putt_distance ?? '');
  const [showClubDropdown, setShowClubDropdown] = useState(false);
  const [showPuttDropdown, setShowPuttDropdown] = useState(false);

  const totalPenalties = penaltyH2O + penaltyOB + penaltyDropped;
  const totalBunkers = bunkerGreenside + bunkerFairway;

  useEffect(() => {
    onDraftChange(holeNumber, {
      strokes, putts, drive_accuracy: drive,
      penalties: totalPenalties,
      penalty_details: { h2o: penaltyH2O, ob: penaltyOB, dropped: penaltyDropped },
      bunkers: totalBunkers,
      bunker_details: { greenside: bunkerGreenside, fairway: bunkerFairway },
      tee_club: teeClub, first_putt_distance: firstPuttDist
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [strokes, putts, drive, penaltyH2O, penaltyOB, penaltyDropped, bunkerGreenside, bunkerFairway, teeClub, firstPuttDist]);

  useEffect(() => {
    const d = draftScores[holeNumber];
    setStrokes(d?.strokes ?? existingScore?.strokes ?? hole.par);
    setPutts(d?.putts ?? existingScore?.putts ?? 2);
    setDrive(d?.drive_accuracy ?? existingScore?.drive_accuracy ?? 'hit');
    setPenaltyH2O(d?.penalty_details?.h2o ?? existingScore?.penalty_details?.h2o ?? 0);
    setPenaltyOB(d?.penalty_details?.ob ?? existingScore?.penalty_details?.ob ?? 0);
    setPenaltyDropped(d?.penalty_details?.dropped ?? existingScore?.penalty_details?.dropped ?? 0);
    setBunkerGreenside(d?.bunker_details?.greenside ?? existingScore?.bunker_details?.greenside ?? 0);
    setBunkerFairway(d?.bunker_details?.fairway ?? existingScore?.bunker_details?.fairway ?? 0);
    setTeeClub(d?.tee_club ?? existingScore?.tee_club ?? '');
    setFirstPuttDist(d?.first_putt_distance ?? existingScore?.first_putt_distance ?? '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [holeNumber]);

  const scoreToPar = strokes - hole.par;
  const scoreLabel = scoreToPar === 0 ? 'Par' : scoreToPar === -1 ? 'Birdie' : scoreToPar === -2 ? 'Eagle' : scoreToPar === -3 ? 'Albatross' : scoreToPar === 1 ? 'Bogey' : scoreToPar === 2 ? 'Double' : scoreToPar > 2 ? `+${scoreToPar}` : `${scoreToPar}`;

  const scoreStyles = scoreToPar <= -2
    ? { color: 'var(--gold)', border: '3px solid var(--gold)', bg: 'rgba(200,169,106,0.08)' }
    : scoreToPar === -1
    ? { color: '#dc2626', border: '3px solid #dc2626', bg: 'rgba(220,38,38,0.05)' }
    : scoreToPar === 0
    ? { color: 'var(--deep-forest)', border: '3px solid var(--stone)', bg: 'transparent' }
    : scoreToPar === 1
    ? { color: '#2563eb', border: '3px solid #2563eb', bg: 'rgba(37,99,235,0.05)' }
    : { color: '#2563eb', border: '3px double #2563eb', bg: 'rgba(37,99,235,0.05)' };

  const scoreShape = scoreToPar < 0 ? 'rounded-full' : scoreToPar > 0 ? 'rounded-lg' : 'rounded-full';

  const handleSave = () => {
    onSave({
      hole_number: holeNumber, par: hole.par, strokes, putts, drive_accuracy: drive,
      penalties: totalPenalties,
      penalty_details: { h2o: penaltyH2O, ob: penaltyOB, dropped: penaltyDropped },
      bunkers: totalBunkers,
      bunker_details: { greenside: bunkerGreenside, fairway: bunkerFairway },
      tee_club: teeClub, first_putt_distance: firstPuttDist
    });
  };

  const CLUBS = ['Driver', '3W', '5W', '3H', '4H', '3i', '4i', '5i', '6i', '7i', '8i', '9i', 'PW', 'GW', 'SW', 'LW'];
  const PUTT_DISTS = ['< 3ft', '3-5ft', '5-10ft', '10-15ft', '15-20ft', '20-30ft', '30-50ft', '50ft+'];

  const Counter = ({ label, value, onDec, onInc, minVal = 0, maxVal = 15 }) => (
    <div className="flex items-center justify-between px-4 py-3">
      <span className="text-sm font-semibold" style={{ color: 'var(--deep-forest)' }}>{label}</span>
      <div className="flex items-center gap-1.5">
        <button onClick={() => onDec(Math.max(minVal, value - 1))} className="w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-90" style={{ background: 'var(--fairway-sand)' }} data-testid={`${label.toLowerCase()}-minus`}>
          <Minus className="w-4 h-4" style={{ color: 'var(--warm-gray)' }} />
        </button>
        <span className="w-12 text-center text-2xl font-black" style={{ color: 'var(--deep-forest)' }}>{value}</span>
        <button onClick={() => onInc(Math.min(maxVal, value + 1))} className="w-11 h-11 rounded-xl flex items-center justify-center transition-all active:scale-90" style={{ background: 'var(--fairway-sand)' }} data-testid={`${label.toLowerCase()}-plus`}>
          <Plus className="w-4 h-4" style={{ color: 'var(--warm-gray)' }} />
        </button>
      </div>
    </div>
  );

  const driveOpts = [
    { key: 'hit', label: 'Fairway', icon: Check, cx: true },
    { key: 'left', label: 'Left', icon: ArrowLeftIcon },
    { key: 'right', label: 'Right', icon: ArrowRightIcon },
    { key: 'long', label: 'Long', icon: ArrowUp },
    { key: 'short', label: 'Short', icon: ArrowDown },
  ];

  const DropdownSelect = ({ label, value, options, isOpen, toggle, onSelect, testId }) => (
    <div className="rounded-xl border overflow-hidden relative mb-3" style={{ background: '#fff', borderColor: 'var(--fairway-sand)' }}>
      <button onClick={toggle} className="w-full flex items-center justify-between px-4 py-3.5" data-testid={testId}>
        <span className="text-sm font-semibold" style={{ color: 'var(--deep-forest)' }}>{label}</span>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold" style={{ color: value ? 'var(--club-green)' : 'var(--warm-gray)' }}>{value || 'Select'}</span>
          <ChevronDown className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} style={{ color: 'var(--warm-gray)' }} />
        </div>
      </button>
      {isOpen && (
        <div className="absolute left-0 right-0 top-full z-30 rounded-xl shadow-xl border max-h-48 overflow-y-auto" style={{ background: '#fff', borderColor: 'var(--fairway-sand)' }} data-testid={`${testId}-list`}>
          {options.map(opt => (
            <button key={opt} onClick={() => onSelect(opt)} className="w-full px-4 py-2.5 text-left text-sm transition-colors" style={{ color: value === opt ? 'var(--club-green)' : 'var(--deep-forest)', background: value === opt ? 'rgba(15,59,46,0.06)' : 'transparent', fontWeight: value === opt ? 700 : 400 }}>
              {opt}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  return (
    <div data-testid="hole-scoring-view">
      {/* Hole header */}
      <div className="rounded-xl border p-4 mb-4" style={{ background: '#fff', borderColor: 'var(--fairway-sand)' }}>
        <div className="flex items-center justify-between">
          <button onClick={onPrev} disabled={holeNumber <= 1} className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-20 transition-all active:scale-90" style={{ background: 'var(--fairway-sand)' }} data-testid="prev-hole">
            <ChevronLeft className="w-5 h-5" style={{ color: 'var(--deep-forest)' }} />
          </button>
          <div className="text-center flex-1">
            <div className="font-serif text-2xl font-bold" style={{ color: 'var(--deep-forest)' }}>Hole {holeNumber}</div>
            <div className="flex gap-2 justify-center mt-1.5">
              {[`Par ${hole.par}`, `${hole.yardage} yds`, ...(hole.handicap > 0 ? [`Hdcp ${hole.handicap}`] : [])].map((tag, i) => (
                <span key={i} className="text-[11px] font-semibold px-2.5 py-0.5 rounded-full" style={{ background: 'var(--fairway-sand)', color: 'var(--warm-gray)' }}>{tag}</span>
              ))}
            </div>
          </div>
          <button onClick={onNext} disabled={holeNumber >= totalHoles} className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-20 transition-all active:scale-90" style={{ background: 'var(--fairway-sand)' }} data-testid="next-hole">
            <ChevronRight className="w-5 h-5" style={{ color: 'var(--deep-forest)' }} />
          </button>
        </div>
      </div>

      {/* Score display */}
      <div className="flex items-center justify-center mb-4">
        <div className={`w-20 h-20 flex flex-col items-center justify-center ${scoreShape} transition-all`} style={{ border: scoreStyles.border, background: scoreStyles.bg }}>
          <span className="text-3xl font-black" style={{ color: scoreStyles.color }}>{strokes}</span>
          <span className="text-[9px] font-bold uppercase" style={{ color: scoreStyles.color }}>{scoreLabel}</span>
        </div>
      </div>

      {/* Strokes & Putts */}
      <div className="rounded-xl border overflow-hidden mb-3" style={{ background: '#fff', borderColor: 'var(--fairway-sand)' }}>
        <Counter label="Strokes" value={strokes} onDec={setStrokes} onInc={setStrokes} minVal={1} maxVal={15} />
        <div className="mx-4 h-px" style={{ background: 'var(--fairway-sand)' }} />
        <Counter label="Putts" value={putts} onDec={setPutts} onInc={(v) => setPutts(Math.min(strokes, v))} maxVal={strokes} />
      </div>

      {/* Tee Shot - Drive Accuracy (par 4+) */}
      {hole.par >= 4 && (
        <div className="rounded-xl border p-4 mb-3" style={{ background: '#fff', borderColor: 'var(--fairway-sand)' }}>
          <span className="text-[11px] font-bold uppercase tracking-wider mb-3 block" style={{ color: 'var(--warm-gray)' }}>Drive Accuracy</span>
          <div className="relative flex items-center justify-center" data-testid="fairway-graphic">
            <div className="relative w-48 h-48">
              {/* Center - Fairway */}
              <button onClick={() => setDrive('hit')} className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[68px] h-[68px] rounded-full flex flex-col items-center justify-center text-xs font-bold transition-all z-10 shadow-sm`} style={{ background: drive === 'hit' ? 'var(--club-green)' : 'var(--fairway-sand)', color: drive === 'hit' ? '#fff' : 'var(--warm-gray)', boxShadow: drive === 'hit' ? '0 4px 16px rgba(15,59,46,0.3)' : 'none' }} data-testid="drive-hit">
                <Check className="w-5 h-5" /><span className="text-[9px] mt-0.5 font-bold">Fairway</span>
              </button>
              {/* Directional buttons */}
              {[
                { key: 'long', pos: 'top-0 left-1/2 -translate-x-1/2', Icon: ArrowUp },
                { key: 'short', pos: 'bottom-0 left-1/2 -translate-x-1/2', Icon: ArrowDown },
                { key: 'left', pos: 'left-0 top-1/2 -translate-y-1/2', Icon: ArrowLeftIcon },
                { key: 'right', pos: 'right-0 top-1/2 -translate-y-1/2', Icon: ArrowRightIcon },
              ].map(d => (
                <button key={d.key} onClick={() => setDrive(d.key)} className={`absolute ${d.pos} w-12 h-12 rounded-full flex flex-col items-center justify-center transition-all`} style={{ background: drive === d.key ? 'var(--burgundy)' : 'var(--fairway-sand)', color: drive === d.key ? '#fff' : 'var(--warm-gray)', boxShadow: drive === d.key ? '0 4px 12px rgba(122,30,44,0.25)' : 'none' }} data-testid={`drive-${d.key}`}>
                  <d.Icon className="w-4 h-4" /><span className="text-[7px] font-bold">{d.key.charAt(0).toUpperCase() + d.key.slice(1)}</span>
                </button>
              ))}
              {/* Connecting lines */}
              <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 192 192">
                <line x1="96" y1="48" x2="96" y2="76" stroke="var(--stone)" strokeWidth="1" strokeDasharray="3 3" />
                <line x1="96" y1="116" x2="96" y2="144" stroke="var(--stone)" strokeWidth="1" strokeDasharray="3 3" />
                <line x1="48" y1="96" x2="76" y2="96" stroke="var(--stone)" strokeWidth="1" strokeDasharray="3 3" />
                <line x1="116" y1="96" x2="144" y2="96" stroke="var(--stone)" strokeWidth="1" strokeDasharray="3 3" />
              </svg>
            </div>
          </div>
        </div>
      )}

      {/* Tee Club & Putt Distance */}
      <DropdownSelect label="Tee Club" value={teeClub} options={CLUBS} isOpen={showClubDropdown} toggle={() => { setShowClubDropdown(!showClubDropdown); setShowPuttDropdown(false); }} onSelect={v => { setTeeClub(v); setShowClubDropdown(false); }} testId="tee-club-dropdown" />
      <DropdownSelect label="1st Putt Distance" value={firstPuttDist} options={PUTT_DISTS} isOpen={showPuttDropdown} toggle={() => { setShowPuttDropdown(!showPuttDropdown); setShowClubDropdown(false); }} onSelect={v => { setFirstPuttDist(v); setShowPuttDropdown(false); }} testId="putt-dist-dropdown" />

      {/* Penalties */}
      <div className="rounded-xl border p-4 mb-3" style={{ background: '#fff', borderColor: 'var(--fairway-sand)' }}>
        <span className="text-[11px] font-bold uppercase tracking-wider mb-3 block" style={{ color: 'var(--warm-gray)' }}>
          Penalties {totalPenalties > 0 && <span style={{ color: 'var(--burgundy)' }}>({totalPenalties})</span>}
        </span>
        <div className="grid grid-cols-3 gap-3" data-testid="penalties-section">
          {[
            { val: penaltyH2O, set: setPenaltyH2O, icon: Droplets, label: 'Water', activeColor: '#2563eb', activeBg: 'rgba(37,99,235,0.08)', testPrefix: 'penalty-h2o' },
            { val: penaltyOB, set: setPenaltyOB, icon: AlertTriangle, label: 'OB', activeColor: 'var(--burgundy)', activeBg: 'rgba(122,30,44,0.06)', testPrefix: 'penalty-ob' },
            { val: penaltyDropped, set: setPenaltyDropped, icon: Circle, label: 'Drop', activeColor: 'var(--gold)', activeBg: 'rgba(200,169,106,0.1)', testPrefix: 'penalty-dropped' },
          ].map(p => (
            <div key={p.testPrefix} className="flex flex-col items-center">
              <button onClick={() => p.set(v => v > 0 ? v : v + 1)} className="w-full aspect-square rounded-xl flex flex-col items-center justify-center transition-all border-2" style={{ borderColor: p.val > 0 ? p.activeColor : 'var(--fairway-sand)', background: p.val > 0 ? p.activeBg : 'var(--fairway-sand)', }} data-testid={`${p.testPrefix}-btn`}>
                <p.icon className="w-6 h-6" style={{ color: p.val > 0 ? p.activeColor : 'var(--warm-gray)' }} />
                {p.val > 0 && <span className="text-xs font-black mt-1" style={{ color: p.activeColor }}>{p.val}</span>}
              </button>
              <span className="text-[10px] font-semibold mt-1.5" style={{ color: 'var(--warm-gray)' }}>{p.label}</span>
              {p.val > 0 && (
                <div className="flex gap-1 mt-1">
                  <button onClick={() => p.set(Math.max(0, p.val - 1))} className="w-6 h-6 rounded flex items-center justify-center" style={{ background: 'var(--fairway-sand)' }} data-testid={`${p.testPrefix}-minus`}><Minus className="w-3 h-3" /></button>
                  <button onClick={() => p.set(p.val + 1)} className="w-6 h-6 rounded flex items-center justify-center" style={{ background: 'var(--fairway-sand)' }} data-testid={`${p.testPrefix}-plus`}><Plus className="w-3 h-3" /></button>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Bunkers - Grint-style SVG illustrations */}
      <div className="rounded-xl border p-4 mb-4" style={{ background: '#fff', borderColor: 'var(--fairway-sand)' }}>
        <span className="text-[11px] font-bold uppercase tracking-wider mb-3 block" style={{ color: 'var(--warm-gray)' }}>
          Bunkers {totalBunkers > 0 && <span style={{ color: 'var(--gold)' }}>({totalBunkers})</span>}
        </span>
        <div className="grid grid-cols-2 gap-4" data-testid="bunkers-section">
          {/* Greenside Bunker */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => setBunkerGreenside(v => v > 0 ? v : v + 1)}
              className="w-full h-32 rounded-xl flex flex-col items-center justify-center transition-all border-2 relative overflow-hidden"
              style={{
                borderColor: bunkerGreenside > 0 ? 'var(--gold)' : 'var(--fairway-sand)',
                background: bunkerGreenside > 0 ? 'rgba(200,169,106,0.06)' : 'var(--fairway-sand)',
              }}
              data-testid="bunker-greenside-btn"
            >
              <svg viewBox="0 0 140 90" className="w-28 h-18">
                {/* Sky hint */}
                <rect x="0" y="0" width="140" height="30" fill="var(--club-cream)" opacity="0.5" />
                {/* Green surface */}
                <ellipse cx="70" cy="28" rx="52" ry="15" fill="#22c55e" opacity={bunkerGreenside > 0 ? 0.3 : 0.12} />
                {/* Flag */}
                <line x1="82" y1="10" x2="82" y2="30" stroke={bunkerGreenside > 0 ? 'var(--deep-forest)' : 'var(--stone)'} strokeWidth="1.5" />
                <path d="M82 10 L94 15 L82 20" fill={bunkerGreenside > 0 ? 'var(--burgundy)' : 'var(--stone)'} />
                {/* Bunker lip */}
                <path d="M22 48 Q42 32 60 40 Q78 48 90 38 Q105 30 118 48" stroke={bunkerGreenside > 0 ? 'var(--gold)' : 'var(--stone)'} strokeWidth="2.5" fill="none" strokeLinecap="round" />
                {/* Sand fill */}
                <ellipse cx="70" cy="60" rx="42" ry="16" fill={bunkerGreenside > 0 ? 'var(--gold)' : 'var(--stone)'} opacity="0.2" />
                {/* Sand texture */}
                {[{x:50,y:56},{x:62,y:62},{x:75,y:54},{x:85,y:60},{x:58,y:50}].map((d,i) => (
                  <circle key={i} cx={d.x} cy={d.y} r="1.2" fill={bunkerGreenside > 0 ? 'var(--gold)' : 'var(--stone)'} opacity="0.35" />
                ))}
              </svg>
              {bunkerGreenside > 0 && <span className="text-sm font-black" style={{ color: 'var(--gold)' }}>{bunkerGreenside}</span>}
            </button>
            <span className="text-xs font-bold mt-2" style={{ color: 'var(--warm-gray)' }}>Greenside</span>
            {bunkerGreenside > 0 && (
              <div className="flex gap-1 mt-1">
                <button onClick={() => setBunkerGreenside(Math.max(0, bunkerGreenside - 1))} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--fairway-sand)' }} data-testid="bunker-greenside-minus"><Minus className="w-3 h-3" /></button>
                <button onClick={() => setBunkerGreenside(bunkerGreenside + 1)} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--fairway-sand)' }} data-testid="bunker-greenside-plus"><Plus className="w-3 h-3" /></button>
              </div>
            )}
          </div>

          {/* Fairway Bunker */}
          <div className="flex flex-col items-center">
            <button
              onClick={() => setBunkerFairway(v => v > 0 ? v : v + 1)}
              className="w-full h-32 rounded-xl flex flex-col items-center justify-center transition-all border-2 relative overflow-hidden"
              style={{
                borderColor: bunkerFairway > 0 ? 'var(--gold)' : 'var(--fairway-sand)',
                background: bunkerFairway > 0 ? 'rgba(200,169,106,0.06)' : 'var(--fairway-sand)',
              }}
              data-testid="bunker-fairway-btn"
            >
              <svg viewBox="0 0 140 90" className="w-28 h-18">
                {/* Fairway grass */}
                <rect x="5" y="18" width="130" height="28" fill="#22c55e" opacity={bunkerFairway > 0 ? 0.15 : 0.07} rx="6" />
                {/* Mow lines */}
                <path d="M10 26 L130 26" stroke="#22c55e" strokeWidth="0.5" opacity="0.2" strokeDasharray="5 4" />
                <path d="M10 38 L130 38" stroke="#22c55e" strokeWidth="0.5" opacity="0.2" strokeDasharray="5 4" />
                {/* Bunker shape */}
                <path d="M28 52 Q48 40 70 46 Q92 52 112 42" stroke={bunkerFairway > 0 ? 'var(--gold)' : 'var(--stone)'} strokeWidth="2" fill="none" strokeLinecap="round" />
                {/* Sand fill */}
                <ellipse cx="70" cy="62" rx="38" ry="14" fill={bunkerFairway > 0 ? 'var(--gold)' : 'var(--stone)'} opacity="0.18" />
                {/* Sand texture */}
                {[{x:55,y:58},{x:68,y:64},{x:80,y:56},{x:62,y:52},{x:78,y:62}].map((d,i) => (
                  <circle key={i} cx={d.x} cy={d.y} r="1.2" fill={bunkerFairway > 0 ? 'var(--gold)' : 'var(--stone)'} opacity="0.35" />
                ))}
                {/* Yard marker */}
                <text x="70" y="14" textAnchor="middle" fontSize="9" fill={bunkerFairway > 0 ? 'var(--gold)' : 'var(--stone)'} fontWeight="700" opacity="0.4">150</text>
              </svg>
              {bunkerFairway > 0 && <span className="text-sm font-black" style={{ color: 'var(--gold)' }}>{bunkerFairway}</span>}
            </button>
            <span className="text-xs font-bold mt-2" style={{ color: 'var(--warm-gray)' }}>Fairway</span>
            {bunkerFairway > 0 && (
              <div className="flex gap-1 mt-1">
                <button onClick={() => setBunkerFairway(Math.max(0, bunkerFairway - 1))} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--fairway-sand)' }} data-testid="bunker-fairway-minus"><Minus className="w-3 h-3" /></button>
                <button onClick={() => setBunkerFairway(bunkerFairway + 1)} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: 'var(--fairway-sand)' }} data-testid="bunker-fairway-plus"><Plus className="w-3 h-3" /></button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Submit */}
      <Button
        className="w-full h-12 font-bold text-sm tracking-wide rounded-xl transition-all btn-press"
        style={{ background: 'var(--club-green)', color: '#fff' }}
        onClick={handleSave}
        data-testid="save-hole-btn"
      >
        <Check className="w-5 h-5 mr-2" /> Submit Hole {holeNumber}
      </Button>
    </div>
  );
};
